import {
	CurrentInteractionState,
	CurrentInteractionActions,
	useCurrentInteractionStore,
	NewInteraction,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types";
import { OutboundVoiceInteraction } from "../outboundVoiceInteraction";
import { OutboundEmailInteraction } from "../outboundEmailInteraction";
import { useStyle } from "@/components/organisms/workspace/style";
import { Flex } from "@/components/atoms/flex";
import { useMemo, useEffect, useCallback } from "react";
import { NewInteractionPanelProps } from "./types";
import { shadowByPosition } from "@/common/theme/styleUtils";
import { useShadowStyle } from "@/common/theme/shadowStyles";
import {
	mergeClasses,
	TabList,
	Tab,
	SelectTabEvent,
	SelectTabData,
	Text,
} from "@fluentui/react-components";
import { PanelHeader } from "@/components/atoms/panelHeader";
import { useIntl } from "react-intl";
import { PeopleFilled, PeopleQueueFilled } from "@fluentui/react-icons";
import { useAllowedOutbound } from "@/hooks/outbound";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";

export const NewInteractionPanel = (props: NewInteractionPanelProps) => {
	const { shadowTo } = props;
	const style = useStyle();
	const intl = useIntl();
	const shadowStyle = useShadowStyle();
	const interaction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state
	);

	const allowedOutbound = useAllowedOutbound();

	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);

	const shadow = useMemo<string | undefined>(
		() => shadowByPosition(shadowTo, shadowStyle),
		[shadowTo, shadowStyle]
	);

	const newInteractionTypeMap = {
		[InteractionType.Call]: <OutboundVoiceInteraction />,
		[InteractionType.Generic]: undefined,
		[InteractionType.Email]: <OutboundEmailInteraction />,
		[InteractionType.Voicemail]: undefined,
		[InteractionType.WhatsApp]: undefined,
		[InteractionType.Webchat]: undefined,
	};

	const type = useMemo(() => {
		return !interaction.new ? InteractionType.Call : interaction.new.type;
	}, [interaction.new]);

	useEffect(() => {
		if (!allowedOutbound.isDataLoaded) {
			return;
		}

		const outboundType = appStoreCache[
			CacheKey.OutboundType
		] as InteractionType;

		if (
			outboundType !== type &&
			allowedOutbound.possibleOutboundTypes.includes(outboundType)
		) {
			selectTab(outboundType);
		}
	}, [
		allowedOutbound.possibleOutboundTypes,
		allowedOutbound.isDataLoaded,
		appStoreCache,
		type,
	]);

	const interactionName = useCallback(
		(interactionType: InteractionType) =>
			intl.formatMessage({
				id: `Interaction.${InteractionType[interactionType]}`,
			}),
		[intl]
	);

	const buildTab = (
		interactionType: InteractionType,
		label: string,
		icon: any
	) => {
		return (
			<Tab
				value={interactionType}
				title={`${label}`}
				icon={icon}
				className={style.tab}
			>
				{interactionName(interactionType)}
			</Tab>
		);
	};

	const selectTab = (interactionType: InteractionType) => {
		const newInteraction: NewInteraction = {
			type: interactionType,
			data: undefined,
		};

		appStoreActions.setCache(CacheKey.OutboundType, interactionType);

		currentInteractionActions.showNew(newInteraction);
	};

	const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
		if (data.value === type) {
			return;
		}

		selectTab(data.value as InteractionType);
	};

	const getIconByType = (interactionType: InteractionType) => {
		if (interactionType === InteractionType.Call) {
			return <PeopleFilled fontSize="16" />;
		} else {
			return <PeopleQueueFilled fontSize="16" />;
		}
	};

	const getTabList = () => {
		return (
			<TabList
				selectedValue={type}
				appearance="filled-circular"
				size="small"
				className={style.tabList}
				onTabSelect={onTabSelect}
			>
				{allowedOutbound.possibleOutboundTypes.map((interactionType) => {
					return buildTab(
						interactionType,
						interactionName(interactionType),
						getIconByType(interactionType)
					);
				})}
			</TabList>
		);
	};

	return (
		<Flex
			column
			className={mergeClasses(
				style.container,
				style.newInteractionContainer,
				shadow
			)}
		>
			<PanelHeader
				left={
					<Text className={style.headerTitle}>
						{intl.formatMessage({ id: "InteractionMenu.New" })}
					</Text>
				}
				right={getTabList()}
			/>
			<Flex column className={style.interactionBarContainer}>
				{newInteractionTypeMap[type]}
			</Flex>
		</Flex>
	);
};
